/**
 * SALESFORGE — Admin Excel Export
 * ─────────────────────────────────────────────────────────────────────────────
 * Erstellt ein vollständiges, farbcodiertes Excel-Workbook direkt im Browser
 * aus echten Audit-Submissions des Admin-Dashboards.
 *
 * Struktur (7 Sheets):
 *  1. 📊 Übersicht        – Alle Submissions auf einen Blick
 *  2. 🔁 Conversion       – C→D / D→P / P→D mit Benchmarks
 *  3. 💰 Umsatz           – Potenzial-Szenarien & Zielerreichung
 *  4. 🔍 Qualitative      – CRM, Reaktionszeit, Pipeline-Hygiene
 *  5. ⚠  Anomalien        – Erkannte Schwachstellen & Warnungen
 *  6. 🛠 Engine-Prüfung   – Methodische Hinweise
 *  7. 📋 Rohdaten         – Alle Felder für eigene Analyse
 */

// ── SheetJS via CDN (kein npm-Install nötig) ──────────────────────────────────
// Wir nutzen eine reine Hilfsfunktion die XLSX via dynamischem Import lädt
// (SheetJS ist via window.XLSX über einen globalen Script-Tag verfügbar,
//  oder wir nutzen eine Inline-Implementierung für kritische Felder)

// Da SheetJS nicht als npm-Paket vorhanden ist, nutzen wir eine elegante
// Alternative: wir generieren ein XML-basiertes Excel (.xlsx) direkt als
// SpreadsheetML — vollständig kompatibel mit Excel/LibreOffice/Google Sheets

type CellStyle = {
  bg?: string;    // ARGB ohne # z.B. "FF1E293B"
  bold?: boolean;
  color?: string; // ARGB Text-Farbe
  align?: 'left' | 'center' | 'right';
  wrap?: boolean;
  border?: boolean;
  numFmt?: string; // z.B. '#,##0 "€"'
  italic?: boolean;
};

interface XLCell {
  v: string | number | null; // value
  t?: 's' | 'n' | 'b';      // type: string, number, bool
  s?: CellStyle;
}

interface XLSheet {
  name: string;
  rows: XLCell[][];
  colWidths?: number[];   // in characters
}

// ── Farb-Konstanten ────────────────────────────────────────────────────────────
const C = {
  DARK:   'FF1E293B',
  SLATE:  'FF334155',
  WHITE:  'FFFFFFFF',
  LIGHT:  'FFF8FAFC',
  MID:    'FFE2E8F0',
  GREEN:  'FF22C55E',
  AMBER:  'FFF59E0B',
  RED:    'FFEF4444',
  BLUE:   'FF3B82F6',
  GREEN_LIGHT: 'FFD1FAE5',
  AMBER_LIGHT: 'FFFEF9C3',
  RED_LIGHT:   'FFFECACA',
  GREEN_MID:   'FF86EFAC',
};

// ── Benchmark-Werte (synchron mit master-engine.ts) ────────────────────────────
const BENCH_C2D = 6.0;
const BENCH_D2P = 50.0;
const BENCH_P2D = 28.0;

// ── Hilfsfunktionen ────────────────────────────────────────────────────────────
function safeNum(v: unknown, fallback = 0): number {
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return isFinite(n) ? n : fallback;
}

function clamp(v: number, lo = 0, hi = 100) { return Math.max(lo, Math.min(hi, v)); }

function fmtEur(v: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

function rateBg(rate: number, bench: number, src: string): string {
  if (src === 'FEHLT' || rate === 0) return 'FFB0B0B0';
  if (rate >= bench * 1.1)  return C.GREEN;
  if (rate >= bench * 0.7)  return C.AMBER;
  return C.RED;
}

function gapBg(gap: number): string {
  if (gap > 50000) return C.RED;
  if (gap > 10000) return C.AMBER;
  return C.GREEN;
}

function compBg(pct: number): string {
  if (pct >= 80) return C.GREEN;
  if (pct >= 50) return C.AMBER;
  return C.RED;
}

// ── Daten-Extraktion aus Submission ───────────────────────────────────────────
function extractSubmissionData(sub: any, statusLabel: string, priorityLabel: string) {
  const cp   = sub.companyProfile || {};
  const res  = sub.result || {};
  const nm   = res.normalizedData || {};
  const cm   = res.computedMetrics || {};
  const fd   = cp.funnelData || {};

  // Volumen
  const contacts      = safeNum(fd.monthlyContacts      ?? nm.monthlyContacts);
  const discovery     = safeNum(fd.monthlyDiscoveryCalls ?? nm.monthlyDiscoveryCalls);
  const presentations = safeNum(fd.monthlyPresentations  ?? nm.monthlyPresentations);
  const deals         = safeNum(fd.monthlyDeals          ?? nm.monthlyDeals);
  const avgDealSize   = safeNum(fd.averageDealSize       ?? nm.averageDealSize, 10000);
  const reps          = safeNum(cp.teamSize ?? fd.productiveReps ?? nm.productiveReps);

  // Berechnete Conversion Rates (aus Volumen wenn möglich)
  let c2d = 0, d2p = 0, p2d = 0;
  let c2dSrc = 'FEHLT', d2pSrc = 'FEHLT', p2dSrc = 'FEHLT';

  if (contacts > 0 && discovery > 0) {
    c2d = clamp(discovery / contacts * 100);
    c2dSrc = 'Volumen';
  } else if (nm.contactToDiscoveryRate != null) {
    c2d = safeNum(nm.contactToDiscoveryRate);
    c2dSrc = 'Direkt';
  }

  if (discovery > 0 && presentations > 0) {
    d2p = clamp(presentations / discovery * 100);
    d2pSrc = 'Volumen';
  } else if (nm.discoveryToPresentationRate != null) {
    d2p = safeNum(nm.discoveryToPresentationRate);
    d2pSrc = 'Direkt';
  }

  if (presentations > 0 && deals > 0) {
    p2d = clamp(deals / presentations * 100);
    p2dSrc = 'Volumen';
  } else if (nm.presentationToCloseRate != null) {
    p2d = safeNum(nm.presentationToCloseRate);
    p2dSrc = 'Direkt';
  }

  // Revenue
  const currentRevenue    = deals * avgDealSize;
  const monthlyGap        = safeNum(cm.monthlyRevenueGap  ?? res.views?.hero?.monthlyRevenueGap);
  const annualGap         = safeNum(cm.annualRevenueGap   ?? res.views?.hero?.annualRevenueGap);
  const realisticRevenue  = currentRevenue + monthlyGap;
  const conservRevenue    = currentRevenue + monthlyGap * 0.5;
  const ambitiousRevenue  = currentRevenue + monthlyGap * 1.25;

  // Datenvollständigkeit
  const knownFields = [
    contacts, discovery, presentations, deals,
    fd.averageDealSize, fd.salesCycleMonths, reps,
    fd.responseTime, fd.followUpDiscipline, fd.pipelineTransparency, fd.leadQuality
  ].filter(f => f !== undefined && f !== null && f !== 0 && f !== '');
  const completeness = Math.round(knownFields.length / 11 * 100);

  // Anomalien
  const anomalies: string[] = [];
  if (c2d > 100) anomalies.push(`⚠ C→D=${c2d.toFixed(1)}% > 100% — Datenfehler`);
  if (d2p > 100) anomalies.push(`⚠ D→P=${d2p.toFixed(1)}% > 100% — Datenfehler`);
  if (p2d > 100) anomalies.push(`⚠ P→D=${p2d.toFixed(1)}% > 100% — Datenfehler`);
  if (deals === 0) anomalies.push('🔴 0 Abschlüsse/Monat → Potenzial aus Fallback-Logik');
  if (completeness <= 40) anomalies.push(`⚠ Datenvollständigkeit nur ${completeness}% → Konfidenz gering`);
  const stale = safeNum(fd.stalePipelineSharePercent);
  if (stale >= 50) anomalies.push(`🔴 ${stale}% stagnierende Pipeline — Forecast-Qualität kritisch`);
  if (!cp.hasCRM && fd.followUpDiscipline === 'ad-hoc') anomalies.push('⚠ Kein CRM + Ad-hoc Follow-ups → Datenverlust wahrscheinlich');
  if (realisticRevenue < currentRevenue - 1) anomalies.push('🔴 ENGINE: Potenzial < Ist — bitte prüfen!');

  // Score
  const diagScore = safeNum(res.overallScore ?? res.percentage ?? res.totalScore);

  return {
    // Meta
    timestamp: sub.timestamp,
    companyName:    String(cp.companyName || '–'),
    industry:       String(cp.industry    || '–'),
    contactName:    `${cp.contactFirstName || ''} ${cp.contactLastName || ''}`.trim() || '–',
    contactEmail:   String(cp.contactEmail || '–'),
    contactPhone:   String(cp.contactPhone || '–'),
    status:         statusLabel,
    priority:       priorityLabel,
    // Volumen
    reps, contacts, discovery, presentations, deals, avgDealSize,
    currentRevenue,
    // Conversion
    c2d: Math.round(c2d * 10) / 10,
    d2p: Math.round(d2p * 10) / 10,
    p2d: Math.round(p2d * 10) / 10,
    c2dSrc, d2pSrc, p2dSrc,
    // Potenzial
    monthlyGap, annualGap,
    realisticRevenue:  Math.round(realisticRevenue),
    conservRevenue:    Math.round(conservRevenue),
    ambitiousRevenue:  Math.round(ambitiousRevenue),
    // Qualitativ
    responseTime:         String(fd.responseTime         || '–'),
    followUpDiscipline:   String(fd.followUpDiscipline   || '–'),
    pipelineTransparency: String(fd.pipelineTransparency || '–'),
    leadQuality:          String(fd.leadQuality          || '–'),
    hasCRM:               cp.hasCRM ? '✓ Ja' : '✗ Nein',
    crmBg:                cp.hasCRM ? C.GREEN : C.RED,
    stalePipeline:        stale > 0 ? stale : null,
    proposalsWithNext:    safeNum(fd.proposalsWithNextStepPercent) || null,
    qualLeadRate:         safeNum(fd.qualifiedLeadRatePercent)     || null,
    decisionMaker:        safeNum(fd.decisionMakerInFirstMeetingPercent) || null,
    salesCycle:           safeNum(fd.salesCycleMonths),
    // Meta
    completeness,
    missingFields: [
      contacts      === 0  ? 'Kontakte'       : '',
      discovery     === 0  ? 'Discovery'      : '',
      presentations === 0  ? 'Präsentationen' : '',
      deals         === 0  ? 'Abschlüsse'     : '',
      !fd.averageDealSize  ? 'Deal-Größe'     : '',
      reps          === 0  ? 'Teamgröße'      : '',
    ].filter(Boolean).join(', ') || '–',
    anomalies,
    anomalyCount:  anomalies.length,
    diagScore,
    // Bewertung
    weakestRate: (c2d/BENCH_C2D <= d2p/BENCH_D2P && c2d/BENCH_C2D <= p2d/BENCH_P2D)
      ? 'C→D' : (d2p/BENCH_D2P <= p2d/BENCH_P2D ? 'D→P' : 'P→D'),
  };
}

// ── SpreadsheetML Generierung ─────────────────────────────────────────────────
// Wir generieren direkt ein .xlsx kompatibles XML-Format ohne externe Library.
// Diese Implementierung nutzt eine bewährte Technik: SpreadsheetML (OOXML).

function escapeXml(s: unknown): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ARGB → Excel style index mapping (simplified inline styles)
function buildStyles(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="8">
    <font><sz val="10"/><name val="Calibri"/></font>
    <font><b/><sz val="10"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><sz val="9"/><name val="Calibri"/></font>
    <font><b/><sz val="14"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><sz val="10"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><i/><sz val="9"/><name val="Calibri"/></font>
  </fonts>
  <fills count="24">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1E293B"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF8FAFC"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF22C55E"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF59E0B"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEF4444"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFD1FAE5"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFEF9C3"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFECACA"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE2E8F0"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF334155"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFB0B0B0"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF86EFAC"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF4ADE80"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFBBF7D0"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF475569"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE0E7FF"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFEF3C7"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF3B82F6"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFBBF7D0"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFDC2626"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFEF3C7"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF0FFF4"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFD1D5DB"/></left>
      <right style="thin"><color rgb="FFD1D5DB"/></right>
      <top style="thin"><color rgb="FFD1D5DB"/></top>
      <bottom style="thin"><color rgb="FFD1D5DB"/></bottom>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="30">
    <xf numFmtId="0"  fontId="0" fillId="0"  borderId="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="1" fillId="2"  borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="3"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="4"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="5"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="6"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="6" fillId="2"  borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="0"  borderId="1" applyBorder="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="5"  borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="6"  borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="4"  borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="7"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="8"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="9"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="1" fillId="0"  borderId="1" applyBorder="1" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="10" borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="5" fillId="11" borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="12" borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="4"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="5"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="6"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="7"  borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="13" borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="4"  fontId="0" fillId="14" borderId="1" applyBorder="1" applyFill="1" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="2" fillId="2"  borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="2" fillId="11" borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="4" fillId="2"  borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="15" borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="0" fillId="6"  borderId="1" applyBorder="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" wrapText="1" vertical="center"/></xf>
    <xf numFmtId="0"  fontId="7" fillId="16" borderId="1" applyBorder="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`;
}

// Style-Index Konstanten (entsprechen den cellXfs oben)
const S = {
  DEFAULT:       0,   // normal, kein Hintergrund, Rahmen
  HDR_DARK_CTR:  1,   // Header dunkel, fett, zentriert, weiß
  LIGHT_CTR:     2,   // hellgrau hintergrund, zentriert
  GREEN_CTR:     3,   // grün
  AMBER_CTR:     4,   // amber
  RED_CTR:       5,   // rot
  HDR_DARK_L:    6,   // Header dunkel, weiß, linksbündig
  NUM_DEFAULT:   7,   // Zahl, kein Hintergrund
  NUM_AMBER:     8,   // Zahl, amber
  NUM_RED:       9,   // Zahl, rot
  NUM_GREEN:     10,  // Zahl, grün
  GREEN_L_CTR:   11,  // hell-grün, zentriert
  AMBER_L_CTR:   12,  // hell-amber
  RED_L_CTR:     13,  // hell-rot
  BOLD_DEFAULT:  14,  // fett, kein Hintergrund
  MID_CTR:       15,  // mid-grau
  SECTION_HDR:   16,  // section header dunkel, links, weiß
  GRAY_CTR:      17,  // grau (nicht bekannt)
  GREEN_LEFT:    18,  // grün, linksbündig
  AMBER_LEFT:    19,  // amber, linksbündig
  RED_LEFT:      20,  // rot, linksbündig
  NUM_GREEN_L:   21,  // Zahl, hellgrün
  NUM_REAL:      22,  // Zahl, grün-mittel
  NUM_AMB:       23,  // Zahl, grün-hell ambitiös
  TITLE:         24,  // Titel groß, dunkel
  SECTION_L:     25,  // section, linksbündig
  TITLE_BIG:     26,  // Sehr groß
  GREEN_CHECK:   27,  // hellgrün check-style
  ANOMALY_RED:   28,  // rot, linksbündig, wrap
  DATE_SLATE:    29,  // slate datum
} as const;

// ── Shared-Strings für SpreadsheetML ─────────────────────────────────────────
class SharedStrings {
  private map = new Map<string, number>();
  private list: string[] = [];

  get(s: string): number {
    if (this.map.has(s)) return this.map.get(s)!;
    const idx = this.list.length;
    this.list.push(s);
    this.map.set(s, idx);
    return idx;
  }

  toXml(): string {
    const items = this.list.map(s =>
      `<si><t xml:space="preserve">${escapeXml(s)}</t></si>`
    ).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${this.list.length}" uniqueCount="${this.list.length}">${items}</sst>`;
  }
}

// ── Worksheet-Generierung ──────────────────────────────────────────────────────
function cellRef(row: number, col: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let c = '';
  let n = col;
  do {
    c = letters[n % 26] + c;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return `${c}${row + 1}`;
}

function buildWorksheet(
  rows: Array<Array<{v: unknown; s?: number; isNum?: boolean}>>,
  colWidths: number[],
  ss: SharedStrings,
  freezeRow = 0,
  freezeCol = 0
): string {
  const colsXml = colWidths.map((w, i) =>
    `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`
  ).join('');

  const rowsXml = rows.map((row, ri) => {
    const ht = ri === 0 ? ' ht="28" customHeight="1"' : ' ht="20" customHeight="1"';
    const cells = row.map((cell, ci) => {
      const ref = cellRef(ri, ci);
      const sAttr = cell.s !== undefined ? ` s="${cell.s}"` : ' s="0"';

      if (cell.v === null || cell.v === undefined || cell.v === '') {
        return `<c r="${ref}"${sAttr}/>`;
      }

      if (cell.isNum || typeof cell.v === 'number') {
        const n = Number(cell.v);
        if (!isFinite(n)) return `<c r="${ref}"${sAttr}/>`;
        return `<c r="${ref}" t="n"${sAttr}><v>${n}</v></c>`;
      }

      // String
      const si = ss.get(String(cell.v));
      return `<c r="${ref}" t="s"${sAttr}><v>${si}</v></c>`;
    }).join('');
    return `<row r="${ri + 1}"${ht}>${cells}</row>`;
  }).join('');

  let freezeXml = '';
  if (freezeRow > 0 || freezeCol > 0) {
    const sqref = cellRef(freezeRow, freezeCol);
    freezeXml = `<sheetViews><sheetView tabSelected="1" workbookViewId="0"><pane xSplit="${freezeCol}" ySplit="${freezeRow}" topLeftCell="${sqref}" activePane="bottomRight" state="frozen"/></sheetView></sheetViews>`;
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
${freezeXml}
<cols>${colsXml}</cols>
<sheetData>${rowsXml}</sheetData>
</worksheet>`;
}

// ── ZIP-Builder (minimale OOXML-Struktur) ────────────────────────────────────
// Wir nutzen eine etablierte Technik: base64-kodiertes Mini-XLSX Template
// Für einen vollständigen ZIP-Builder im Browser nutzen wir fflate (bereits
// häufig in Projekten) oder einen eingebetteten Micro-Zipper.

// Da wir kein externes ZIP-Package haben, nutzen wir die Browser-native
// CompressionStream API (verfügbar in modernen Browsern)
// Fallback: wir generieren einen CSV-Blob mit strukturiertem HTML als Alternative

// ── Haupt-Export-Funktion ─────────────────────────────────────────────────────
export async function exportAdminExcel(
  submissions: any[],
  getStatusFn: (id: string) => { status: string; priority: string },
  statusLabels: Record<string, string>,
  priorityLabels: Record<string, string>
): Promise<void> {
  if (submissions.length === 0) {
    throw new Error('Keine Submissions zum Exportieren vorhanden');
  }

  // Daten aufbereiten
  const data = submissions.map(sub => {
    const st = getStatusFn(sub.id);
    return extractSubmissionData(
      sub,
      statusLabels[st.status] || st.status,
      priorityLabels[st.priority] || st.priority
    );
  });

  // Dynamisch SheetJS laden (CDN-Fallback)
  let XLSX: any = null;
  try {
    // Versuche XLSX aus global (falls geladen)
    XLSX = (window as any).XLSX;
    if (!XLSX) {
      // Lade SheetJS dynamisch via CDN
      await new Promise<void>((resolve, reject) => {
        if ((window as any).XLSX) { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('SheetJS konnte nicht geladen werden'));
        document.head.appendChild(script);
      });
      XLSX = (window as any).XLSX;
    }
  } catch (e) {
    console.warn('SheetJS nicht verfügbar, fallback to CSV');
    XLSX = null;
  }

  if (!XLSX) {
    // Fallback: strukturiertes CSV
    exportFallbackCSV(data, submissions, getStatusFn, statusLabels, priorityLabels);
    return;
  }

  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Übersicht ──────────────────────────────────────────────────────
  {
    const headers = [
      '#', 'Datum', 'Unternehmen', 'Branche', 'Ansprechpartner', 'E-Mail',
      'Reps', 'Deals/Mo', 'Ist-Rev. €/Mo', 'C→D %', 'D→P %', 'P→D %',
      'Monatl. Lücke €', 'Jährl. Lücke €', 'Vollst. %', 'Status', 'Priorität', 'Anomalien'
    ];

    const rows = data.map((d, i) => [
      i + 1,
      new Date(d.timestamp).toLocaleDateString('de-DE'),
      d.companyName,
      d.industry,
      d.contactName,
      d.contactEmail,
      d.reps || '?',
      d.deals,
      d.currentRevenue,
      `${d.c2d}%`,
      `${d.d2p}%`,
      `${d.p2d}%`,
      d.monthlyGap,
      d.annualGap,
      `${d.completeness}%`,
      d.status,
      d.priority,
      d.anomalyCount > 0 ? `${d.anomalyCount} Hinweis(e)` : '✓ OK',
    ]);

    const ws1 = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Spaltenbreiten
    ws1['!cols'] = [
      {wch: 4}, {wch: 12}, {wch: 22}, {wch: 18}, {wch: 22}, {wch: 28},
      {wch: 6}, {wch: 9}, {wch: 14}, {wch: 8}, {wch: 8}, {wch: 8},
      {wch: 14}, {wch: 14}, {wch: 11}, {wch: 12}, {wch: 10}, {wch: 16}
    ];

    applyHeaderStyle(ws1, headers.length, XLSX);
    applyDataStyles(ws1, data, 1, XLSX);
    XLSX.utils.book_append_sheet(wb, ws1, '📊 Übersicht');
  }

  // ── Sheet 2: Conversion Detail ──────────────────────────────────────────────
  {
    const headers = [
      '#', 'Unternehmen',
      'C→D Ist %', 'C→D Bench %', 'C→D Delta', 'C→D Quelle',
      'D→P Ist %', 'D→P Bench %', 'D→P Delta', 'D→P Quelle',
      'P→D Ist %', 'P→D Bench %', 'P→D Delta', 'P→D Quelle',
      'Schwächste Rate', 'Bewertung'
    ];

    const rows = data.map((d, i) => {
      const c2dDelta = d.c2d - BENCH_C2D;
      const d2pDelta = d.d2p - BENCH_D2P;
      const p2dDelta = d.p2d - BENCH_P2D;

      const rateRatios = {
        'C→D': d.c2d / Math.max(0.001, BENCH_C2D),
        'D→P': d.d2p / Math.max(0.001, BENCH_D2P),
        'P→D': d.p2d / Math.max(0.001, BENCH_P2D),
      };
      const minRatio = Math.min(...Object.values(rateRatios));
      const assessment = minRatio < 0.5 ? '🔴 Kritisch'
        : minRatio < 0.85 ? '🟠 Ausbaufähig'
        : minRatio < 1.0  ? '🟡 Nah am Benchmark'
        : '🟢 Über Benchmark';

      return [
        i + 1, d.companyName,
        `${d.c2d}%`, `${BENCH_C2D}%`, `${c2dDelta >= 0 ? '+' : ''}${c2dDelta.toFixed(1)}%`, d.c2dSrc,
        `${d.d2p}%`, `${BENCH_D2P}%`, `${d2pDelta >= 0 ? '+' : ''}${d2pDelta.toFixed(1)}%`, d.d2pSrc,
        `${d.p2d}%`, `${BENCH_P2D}%`, `${p2dDelta >= 0 ? '+' : ''}${p2dDelta.toFixed(1)}%`, d.p2dSrc,
        d.weakestRate, assessment
      ];
    });

    const ws2 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws2['!cols'] = [
      {wch:4},{wch:22},{wch:9},{wch:10},{wch:9},{wch:10},
      {wch:9},{wch:10},{wch:9},{wch:10},{wch:9},{wch:10},
      {wch:9},{wch:10},{wch:14},{wch:22}
    ];
    applyHeaderStyle(ws2, headers.length, XLSX);
    XLSX.utils.book_append_sheet(wb, ws2, '🔁 Conversion');
  }

  // ── Sheet 3: Umsatz & Potenzial ─────────────────────────────────────────────
  {
    const headers = [
      '#', 'Unternehmen', 'Ist-Rev. €/Mo',
      'Konservativ €', 'Realistisch €', 'Ambitiös €',
      'Monatl. Lücke €', 'Jährl. Lücke €',
      'Ansprechpartner', 'E-Mail', 'Telefon',
      'Status', 'Priorität', 'Score'
    ];

    const rows = data.map((d, i) => [
      i + 1, d.companyName, d.currentRevenue,
      d.conservRevenue, d.realisticRevenue, d.ambitiousRevenue,
      d.monthlyGap, d.annualGap,
      d.contactName, d.contactEmail, d.contactPhone,
      d.status, d.priority,
      d.diagScore > 0 ? `${Math.round(d.diagScore)}%` : '–'
    ]);

    const ws3 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws3['!cols'] = [
      {wch:4},{wch:22},{wch:14},{wch:14},{wch:14},{wch:14},
      {wch:14},{wch:14},{wch:22},{wch:28},{wch:16},{wch:12},{wch:10},{wch:8}
    ];
    applyHeaderStyle(ws3, headers.length, XLSX);
    XLSX.utils.book_append_sheet(wb, ws3, '💰 Umsatz & Potenzial');
  }

  // ── Sheet 4: Qualitative Signale ────────────────────────────────────────────
  {
    const headers = [
      '#', 'Unternehmen', 'CRM', 'Reaktionszeit', 'Follow-up',
      'Pipeline-Transparenz', 'Lead-Qualität',
      'Sales Cycle (Mo)', 'Stagnierte Pipeline %',
      'Angebote m. Next Step %', 'Entscheider Erstgespräch %', 'Qual. Lead Rate %'
    ];

    const rows = data.map((d, i) => [
      i + 1, d.companyName, d.hasCRM,
      d.responseTime, d.followUpDiscipline,
      d.pipelineTransparency, d.leadQuality,
      d.salesCycle || '?',
      d.stalePipeline !== null ? `${d.stalePipeline}%` : '?',
      d.proposalsWithNext !== null ? `${d.proposalsWithNext}%` : '?',
      d.decisionMaker !== null ? `${d.decisionMaker}%` : '?',
      d.qualLeadRate !== null ? `${d.qualLeadRate}%` : '?',
    ]);

    const ws4 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws4['!cols'] = [
      {wch:4},{wch:22},{wch:8},{wch:14},{wch:14},
      {wch:20},{wch:14},{wch:14},{wch:20},{wch:22},{wch:26},{wch:18}
    ];
    applyHeaderStyle(ws4, headers.length, XLSX);
    XLSX.utils.book_append_sheet(wb, ws4, '🔍 Qualitative Signale');
  }

  // ── Sheet 5: Anomalien ──────────────────────────────────────────────────────
  {
    const anomRows: any[][] = [
      ['#', 'Unternehmen', 'Datum', 'Vollst. %', 'Anomalie', 'Empfehlung']
    ];

    data.forEach((d, i) => {
      if (d.anomalies.length === 0) {
        anomRows.push([i + 1, d.companyName,
          new Date(d.timestamp).toLocaleDateString('de-DE'),
          `${d.completeness}%`, '✅ Keine Anomalien', '–']);
      } else {
        d.anomalies.forEach((a: string, ai: number) => {
          const rec = a.includes('C→D') ? 'Dateneingabe prüfen'
            : a.includes('0 Abschlüsse') ? '25% Uplift-Fallback aktiv — keine echten Daten'
            : a.includes('ENGINE') ? '🔴 Engine-Fehler — bitte melden'
            : a.includes('CRM') ? 'CRM einführen ist höchste Priorität'
            : a.includes('Vollständigkeit') ? 'Mehr Felder im Audit ausfüllen'
            : a.includes('stagnierend') ? 'Pipeline-Hygiene einführen'
            : 'Prüfen und korrigieren';
          anomRows.push([
            ai === 0 ? i + 1 : '', ai === 0 ? d.companyName : '',
            ai === 0 ? new Date(d.timestamp).toLocaleDateString('de-DE') : '',
            ai === 0 ? `${d.completeness}%` : '',
            a, rec
          ]);
        });
      }
    });

    const ws5 = XLSX.utils.aoa_to_sheet(anomRows);
    ws5['!cols'] = [{wch:4},{wch:22},{wch:12},{wch:12},{wch:60},{wch:40}];
    applyHeaderStyle(ws5, 6, XLSX);
    XLSX.utils.book_append_sheet(wb, ws5, '⚠ Anomalien');
  }

  // ── Sheet 6: Engine-Befunde ─────────────────────────────────────────────────
  {
    const engineRows = [
      ['Befund', 'Beschreibung', 'Schwere', 'Empfehlung'],
      ['Gap-Quelle bei 0 Deals', 'Wenn 0 Abschlüsse/Monat: Revenue=0, Potenzial aus 25%-Uplift-Fallback', 'MITTEL', 'UI sollte Warnung zeigen dass kein Messwert vorliegt'],
      ['Rates >100% möglich', 'Bei direkter Eingabe von Rates kann theoretisch >100% eingegeben werden', 'MITTEL', 'Validierung im Wizard: max. 100% erzwingen'],
      ['Ambitiös = Realistisch', 'Wenn Ist > Benchmark: improvedRate = currentRate → kein Uplift', 'GERING', 'UI: "Kein weiteres Potenzial identifiziert" anzeigen'],
      ['Datum-Abhängigkeit', 'projectedYearEnd verwendet aktuellen Jahrestag → täglich verschiedene Ergebnisse', 'MITTEL', 'Für Reports: Datum im Snapshot einfrieren'],
      ['Kein Jahresziel', 'Ohne totalRevenueTargetYear kein Forecast-Delta sichtbar', 'INFO', 'Jahresziel-Feld im Wizard prominenter machen'],
      ['Kapazitäts-Check fehlt', '1 Rep + hohes Ziel: Engine zeigt Gap aber kein "Brauche mehr Reps" Signal', 'MITTEL', 'repExecutionPath: Kapazitäts-Warnung hinzufügen'],
      ['0% statt ? für unbekannte Rates', 'Wenn Rate unbekannt: UI zeigt 0% statt "?" — täuschend', 'MITTEL', 'In ResultsMaster: null vs. 0 unterscheiden'],
      ['DEFAULT_FORM _unknown Flags', 'biggestDropoff etc. ohne _unknown-Flag-Default in DEFAULT_FORM', 'MITTEL', 'Für alle Pflichtfelder: _unknown: false als Default setzen'],
    ];

    const ws6 = XLSX.utils.aoa_to_sheet(engineRows);
    ws6['!cols'] = [{wch:28},{wch:55},{wch:10},{wch:45}];
    applyHeaderStyle(ws6, 4, XLSX);
    XLSX.utils.book_append_sheet(wb, ws6, '🛠 Engine-Befunde');
  }

  // ── Sheet 7: Rohdaten ───────────────────────────────────────────────────────
  {
    const headers = [
      '#', 'Datum', 'Unternehmen', 'Branche', 'Vorname', 'Nachname', 'E-Mail', 'Telefon', 'Position',
      'Reps', 'Kontakte/Mo', 'Discovery/Mo', 'Präs./Mo', 'Deals/Mo', 'Deal-Größe €',
      'Ist-Rev. €/Mo', 'C2D%', 'D2P%', 'P2D%', 'C2D Src', 'D2P Src', 'P2D Src',
      'Monatl. Lücke €', 'Jährl. Lücke €', 'Realist. €', 'Konserv. €', 'Ambitiös €',
      'Reaktionszeit', 'Follow-Up', 'Pipeline-Transp.', 'Lead-Qualität', 'CRM',
      'Stagniert %', 'Next-Step %', 'Entscheider %', 'Qual. Lead %', 'Sales Cycle Mo',
      'Vollst. %', 'Fehlende Felder', 'Status', 'Priorität', 'Score', 'Anomalien'
    ];

    const rows = submissions.map((sub, i) => {
      const d = data[i];
      const cp = sub.companyProfile || {};
      return [
        i + 1,
        new Date(sub.timestamp).toLocaleDateString('de-DE'),
        d.companyName, d.industry,
        cp.contactFirstName || '–', cp.contactLastName || '–',
        d.contactEmail, d.contactPhone, cp.contactPosition || '–',
        d.reps || '?', d.contacts || '?', d.discovery || '?',
        d.presentations || '?', d.deals, d.avgDealSize,
        d.currentRevenue, d.c2d, d.d2p, d.p2d, d.c2dSrc, d.d2pSrc, d.p2dSrc,
        d.monthlyGap, d.annualGap, d.realisticRevenue, d.conservRevenue, d.ambitiousRevenue,
        d.responseTime, d.followUpDiscipline, d.pipelineTransparency, d.leadQuality, d.hasCRM,
        d.stalePipeline ?? '?', d.proposalsWithNext ?? '?',
        d.decisionMaker ?? '?', d.qualLeadRate ?? '?', d.salesCycle || '?',
        `${d.completeness}%`, d.missingFields, d.status, d.priority,
        d.diagScore > 0 ? `${Math.round(d.diagScore)}%` : '–',
        d.anomalies.join(' | ') || '–'
      ];
    });

    const ws7 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws7['!cols'] = headers.map(() => ({wch: 15}));
    applyHeaderStyle(ws7, headers.length, XLSX);
    XLSX.utils.book_append_sheet(wb, ws7, '📋 Rohdaten');
  }

  // ── Download auslösen ───────────────────────────────────────────────────────
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
  const filename = `Salesforge_Audit_Report_${timestamp}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ── Style-Helfer ───────────────────────────────────────────────────────────────
function applyHeaderStyle(ws: any, numCols: number, XLSX: any) {
  // Erste Zeile fett + dunkel
  for (let c = 0; c < numCols; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[ref]) ws[ref] = { v: '', t: 's' };
    ws[ref].s = {
      fill: { patternType: 'solid', fgColor: { rgb: '1E293B' } },
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D1D5DB' } },
        bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
        left: { style: 'thin', color: { rgb: 'D1D5DB' } },
        right: { style: 'thin', color: { rgb: 'D1D5DB' } },
      }
    };
  }
}

function applyDataStyles(ws: any, data: any[], startRow: number, XLSX: any) {
  data.forEach((d, ri) => {
    const row = startRow + ri;
    const isEven = ri % 2 === 0;
    const rowBg = isEven ? 'F8FAFC' : 'FFFFFF';

    // Anomalien-Spalte (Index 17 in Übersicht)
    const anomRef = XLSX.utils.encode_cell({ r: row, c: 17 });
    if (ws[anomRef]) {
      ws[anomRef].s = {
        fill: { patternType: 'solid', fgColor: { rgb: d.anomalyCount > 0 ? (d.anomalyCount >= 3 ? 'FECACA' : 'FEF3C7') : 'D1FAE5' } },
        font: { sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }

    // Gap-Spalten (12, 13) - Ampel
    const gapBgColor = d.monthlyGap > 50000 ? 'FECACA' : d.monthlyGap > 10000 ? 'FEF9C3' : 'D1FAE5';
    [12, 13].forEach(c => {
      const ref = XLSX.utils.encode_cell({ r: row, c });
      if (ws[ref]) {
        ws[ref].s = {
          fill: { patternType: 'solid', fgColor: { rgb: gapBgColor } },
          font: { sz: 10, bold: true },
          alignment: { horizontal: 'center', vertical: 'center' },
          numFmt: '#,##0',
        };
      }
    });

    // Vollständigkeit-Spalte (14) - Ampel
    const compRef = XLSX.utils.encode_cell({ r: row, c: 14 });
    if (ws[compRef]) {
      ws[compRef].s = {
        fill: { patternType: 'solid', fgColor: { rgb: d.completeness >= 80 ? '22C55E' : d.completeness >= 50 ? 'F59E0B' : 'EF4444' } },
        font: { sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  });
}

// ── CSV Fallback ───────────────────────────────────────────────────────────────
function exportFallbackCSV(
  data: any[],
  submissions: any[],
  getStatusFn: (id: string) => any,
  statusLabels: Record<string, string>,
  priorityLabels: Record<string, string>
) {
  const BOM = '\uFEFF';
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    return s.includes(';') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const headers = [
    'Datum', 'Unternehmen', 'Branche', 'Ansprechpartner', 'E-Mail',
    'Deals/Mo', 'Ist-Rev €/Mo', 'C→D%', 'D→P%', 'P→D%',
    'Monatl. Lücke €', 'Jährl. Lücke €', 'Vollst. %', 'Status', 'Priorität', 'Anomalien'
  ];

  const rows = data.map(d => [
    new Date(d.timestamp).toLocaleDateString('de-DE'),
    d.companyName, d.industry, d.contactName, d.contactEmail,
    d.deals, d.currentRevenue, `${d.c2d}%`, `${d.d2p}%`, `${d.p2d}%`,
    d.monthlyGap, d.annualGap, `${d.completeness}%`,
    d.status, d.priority, d.anomalies.join(' | ') || '–'
  ].map(esc).join(';'));

  const csv = BOM + [headers.map(esc).join(';'), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Salesforge_Audit_Report_${Date.now()}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
